import Page from "../../Components/Page";
import ReactTable from "../../Components/ReactTable";
import React, {useEffect, useState} from "react";
import moment from "moment";
import {makeApolloClientCall} from "../../../Api/makeApolloApiCall";
import {LoadingWrapper} from "../../Components/LoadingWrapper";
import {makeApiCall} from "../../../Api/makeApiCall";
import RetailerIdAndName from "../../Components/RetailerIdAndName";
import {getRetailerName} from "../../../utils/localCache/retailerMapCache";
import csvDownloadWithNulls from "../../../utils/CsvDownloadWithNulls";
import _ from "lodash";
import {faIcons} from "../../../utils/iconConstants";
import SelectDropdown from "../../Components/Dropdowns/SelectDropdown";

export const retailerInfoGqlQuery = `
        query retailers {
            salesforceRetailers {
                retailerId
                status
                posSystem
            }
        }`;

const DailySalesMissingCheck = () => {

    const [startWeekId, setStartWeekId] = useState(undefined);
    const [startWeekIdOptions, setStartWeekIdOptions] = useState([]);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    const daysOfWeek = {
        'Sunday': 0,
        'Monday': 1,
        'Tuesday': 2,
        'Wednesday': 3,
        'Thursday': 4,
        'Friday': 5,
        'Saturday': 6
    }

    const convertDateToDayOfWeek = (row, dayOfWeek) => {
        const foundDate = row.rolledUpDates.find(date => moment(date).day() === dayOfWeek);
        return foundDate ? moment(foundDate).format('YYYY-MM-DD') : 'Missing';
    };

    const customColumns = [
        {
            header: "Retailer",
            id: "retailerIdName",
            filterable: true,
            accessorKey: "retailerId",
            sticky: "left",
            size: 250,
            cell: ({row}) => <RetailerIdAndName retailerId={row.original.retailerId}/>,
            filterFn: (row, columnIds, filterValue) => !filterValue || Boolean(`${row.original.retailerId} - ${getRetailerName(row.original.retailerId)?.toLowerCase()}`.includes(filterValue.toLowerCase()))
        },
        {header: 'Store Id', accessorKey: 'spinsStoreId', sticky: "left", size: 100},
        {header: 'Store Name', accessorKey: 'storeName', sticky: "left", size: 250},
        {header: 'Roll Up', accessorKey: 'rollupDayOfWeek', sticky: "left", size: 100},
        {header: 'Min Days', accessorKey: 'minDaysOfSales', sticky: "left", size: 100},
        {header: 'Receipt Lag', accessorKey: 'receiptLag', sticky: "left", size: 120},
        {header: 'External Store Id', accessorKey: 'externalStoreId'},
        {header: 'POS System', accessorKey: 'posSystem'},
        {header: 'Week', accessorKey: 'salesEndDate'},
        {header: 'Monday', id: 'monday', accessorFn: (row) => convertDateToDayOfWeek(row, daysOfWeek["Monday"])},
        {header: 'Tuesday', id: 'tuesday', accessorFn: (row) => convertDateToDayOfWeek(row, daysOfWeek["Tuesday"])},
        {
            header: 'Wednesday',
            id: 'wednesday',
            accessorFn: (row) => convertDateToDayOfWeek(row, daysOfWeek["Wednesday"])
        },
        {header: 'Thursday', id: 'thursday', accessorFn: (row) => convertDateToDayOfWeek(row, daysOfWeek["Thursday"])},
        {header: 'Friday', id: 'friday', accessorFn: (row) => convertDateToDayOfWeek(row, daysOfWeek["Friday"])},
        {header: 'Saturday', id: 'saturday', accessorFn: (row) => convertDateToDayOfWeek(row, daysOfWeek["Saturday"])},
        {header: 'Sunday', id: 'sunday', accessorFn: (row) => convertDateToDayOfWeek(row, daysOfWeek["Sunday"])},
    ]

    useEffect(() => {
        if (!startWeekId) return;

        setLoading(true);
        setError(false);

        Promise.all([
            makeApolloClientCall(retailerInfoGqlQuery),
            makeApiCall(`${process.env.REACT_APP_EDP_API_BASE_URL}/dailySalesMissingCheck/?start_week_id=${startWeekId}`),
        ])
            .then(([
                       retailerInfoResponse,
                       dailySalesMissingCheckQueryResponse
                   ]) => {
                const retailerMap = [...retailerInfoResponse.salesforceRetailers].reduce((acc, curr) => {
                    acc[curr.retailerId] = {...acc[curr.retailerId], ...curr};
                    return acc;
                }, {});
                const filteredData = dailySalesMissingCheckQueryResponse?.data;
                const enrichedData = filteredData.map(store => {
                    const foundRetailerInfo = retailerMap[store.retailerId];
                    return {...store, posSystem: foundRetailerInfo?.posSystem,}
                })
                setData(enrichedData);
            })
            .catch(() => setError(true))
            .finally(() => setLoading(false))
    }, [startWeekId])

    const getWeekQuery = (date, priorWeeks) => `getPriorPeriodFromDate(date: "${date}", periodsPrior: ${priorWeeks}, periodType: WEEK) { endDate weekId }`

    useEffect(() => {
        const today = moment(Date.now()).format('yyyy-MM-DD');
        const weekOptionsQuery = `
            query {
              lastWeek: ${getWeekQuery(today, 1)}
              twoWeek: ${getWeekQuery(today, 2)}
              threeWeek: ${getWeekQuery(today, 3)}
              fourWeek: ${getWeekQuery(today, 4)}
            }
        `

        makeApolloClientCall(weekOptionsQuery)
            .then(weekOptionsQueryResponse => {
                const weekOptionsArray = Object.values(weekOptionsQueryResponse)
                    .map(({weekId, endDate}) => ({value: weekId, label: endDate}));
                setStartWeekIdOptions(weekOptionsArray);
            });
    }, [])

    const downloadCsv = (rows) => {
        setIsDownloading(true);
        const clonedData = _.cloneDeep(rows)
        const downloadData = clonedData.map(x => {
            const data = x.original
            const monday = convertDateToDayOfWeek(data, daysOfWeek["Monday"])
            const tuesday = convertDateToDayOfWeek(data, daysOfWeek["Tuesday"])
            const wednesday = convertDateToDayOfWeek(data, daysOfWeek["Wednesday"])
            const thursday = convertDateToDayOfWeek(data, daysOfWeek["Thursday"])
            const friday = convertDateToDayOfWeek(data, daysOfWeek["Friday"])
            const saturday = convertDateToDayOfWeek(data, daysOfWeek["Saturday"])
            const sunday = convertDateToDayOfWeek(data, daysOfWeek["Sunday"])
            delete data.rolledUpDates
            return {
                ...data,
                monday,
                tuesday,
                wednesday,
                thursday,
                friday,
                saturday,
                sunday
            };
        })
        csvDownloadWithNulls(downloadData, `dailySalesMissingCheck_${startWeekId}.csv`, null)
        setIsDownloading(false);
    }

    const exclusionsDropdown =
        <div className={'title-object'}>
            <SelectDropdown
                options={[
                    {label: 'Retailers', value: 'Retailers', url: '/missingDataRetailerExclusions'},
                    {label: 'Stores', value: 'Stores', url: '/configurations/store/missingDataExclusions'}
                ]}
                id={'exclusion_dropdown'}
                selectValue={'Review Exclusions ...'}
                onChange={o => window.open(o.url, '_blank')}
            />
        </div>

    return <Page name={"Daily Missing Data"} beta={true} headerSubComponent={exclusionsDropdown}>
        <div className="daily-sales-missing-start-week-dropdown-container">
            <SelectDropdown id={"start_week_dropdown"}
                            dataTestId={"start_week_dropdown"}
                            inputLabel={"Start Week"}
                            selectValue={startWeekId}
                            selectLabel={startWeekId && startWeekIdOptions?.find(option => option.value.toString() === startWeekId.toString())?.label}
                            options={startWeekIdOptions}
                            onChange={(option) => setStartWeekId(option.value)}
            />
        </div>
        <LoadingWrapper loading={loading}>
            {!error && !_.isEmpty(data) &&
                <ReactTable filterable={true}
                            columns={customColumns}
                            data={data}
                            getTdProps={(rowInfo, table, cellId) => rowInfo.getValue(cellId) === 'Missing' ? "timeService-invalid-week" : ""}
                            initialState={{sorting: [{id: 'retailerIdName', desc: true}, {id: 'spinsStoreId', desc: true}], pagination: {pageSize: 10}}}
                >
                    {({rows}) => {
                        return (rows.length > 0 &&
                            <div className={"daily-sales-missing-download-csv"}>
                                <button className={"hollow button"}
                                        disabled={isDownloading}
                                        onClick={() => downloadCsv(rows)}>
                                    {!isDownloading && 'Download CSV'}
                                    {isDownloading && (<><i className={`${faIcons.spinner} fa-spin`} data-testid={"spinner"}/><span>Loading</span></>)}
                                </button>
                            </div>
                        )
                    }}
                </ReactTable>}
            {error && <div className={'error-message'} data-testid={'error-div'}>Error loading data</div>}
        </LoadingWrapper>
    </Page>;
};

export default DailySalesMissingCheck;